import {inject, Injectable} from '@angular/core';
import {
  AttributeFulfillmentTypeDirectus,
  DirectusService,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
  SubcriteriaAttributeDirectus,
} from '@frontend/directus';
import {
  AttributeWithScores,
  EvaluationCriteriaResponse,
  EvaluationSubCriteriaImportanceScore,
  ProviderScoresResponse,
  SubCriteriaWithScores,
  TenderSubcriteriaAttributeView,
  TenderSubcriteriaAttributesResponse,
} from '@frontend/shared-api';
import {SubcriteriaDataService} from './subcriteria-data.service';
import {TabManagementService} from '../tabs/tab-management.service';
import {
  DirectusDataMaps,
  ECTab,
  ScoreMapEntry,
  SubcriteriaAttribute,
  SubcriteriaTab,
  TabBuilderOptions,
} from '../components/provider-questionnaire/provider-questionnaire.types';
import {isEC3} from '../constants/evaluation-criteria.constants';
import {SubCriteriaConfig} from '../model/sub-criteria.interfaces';
import {
  AttributesByEvaluationCriteriaMap,
  SubCriteriaAttributeItem,
  AttributesBySubCriteriaMap,
} from '../components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';

export interface NormalizedAttributeData {
  attributeId: number;
  directusId: string;
  scaNumber: number;
  score: number | undefined;
  scoreAbsent: boolean;
  comment?: string;
  importanceScore: EvaluationSubCriteriaImportanceScore;
  subcriteriaId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AttributeTransformationService {
  private readonly subcriteriaDataService = inject(SubcriteriaDataService);
  private readonly tabManagementService = inject(TabManagementService);
  private readonly directusService = inject(DirectusService);

  transformDirectusAttribute(
    directusAttr: SubcriteriaAttributeDirectus,
    fulfillmentTypeMap: Map<number, AttributeFulfillmentTypeDirectus>,
    attributeId?: number,
  ): SubcriteriaAttribute {
    const fulfillmentType = directusAttr.fulfillment_type
      ? fulfillmentTypeMap.get(directusAttr.fulfillment_type)?.type
      : undefined;

    return {
      attributeId: attributeId || Number.parseInt(directusAttr.id, 10) || 0,
      directusId: directusAttr.id,
      scaNumber: directusAttr.sca_number,
      importanceScore: 'template',
      translations: directusAttr.translations || [],
      fulfillmentType,
      score: undefined,
      scoreAbsent: false,
      hasChanges: false,
    };
  }

  transformTenderAttribute(
    tenderAttr: TenderSubcriteriaAttributeView,
    directusAttr: SubcriteriaAttributeDirectus,
    fulfillmentTypeMap: Map<number, AttributeFulfillmentTypeDirectus>,
  ): SubcriteriaAttribute {
    const fulfillmentType = directusAttr.fulfillment_type
      ? fulfillmentTypeMap.get(directusAttr.fulfillment_type)?.type
      : undefined;

    return {
      ...tenderAttr,
      translations: directusAttr.translations || [],
      fulfillmentType,
      hasChanges: false,
    };
  }

  getImportanceScoreTranslationKey(importanceScore: string): string {
    const translationMap: {[key: string]: string} = {
      BASIC_ATTRIBUTE: 'evaluation.importanceScores.basicAttribute',
      PERFORMANCE_ATTRIBUTE: 'evaluation.importanceScores.performanceAttribute',
      ENTHUSIASM_ATTRIBUTE: 'evaluation.importanceScores.enthusiasmAttribute',
      INDIFFERENT_ATTRIBUTE: 'evaluation.importanceScores.indifferentAttribute',
      NOT_RELEVANT: 'evaluation.importanceScores.notRelevant',
    };

    return translationMap[importanceScore] || importanceScore;
  }

  normalizeProviderScoreAttribute(
    attributeWithScores: AttributeWithScores,
  ): NormalizedAttributeData | null {
    const score =
      attributeWithScores.scores.length > 0 ? attributeWithScores.scores[0].score : undefined;

    if (score === undefined || score === null) {
      return null;
    }
    const importanceScore =
      attributeWithScores.importanceScores.length > 0
        ? attributeWithScores.importanceScores[0].importanceScore
        : undefined;

    if (!importanceScore) {
      return null;
    }

    return {
      attributeId: attributeWithScores.id,
      directusId: attributeWithScores.directusId,
      scaNumber: attributeWithScores.scaNumber,
      score: score,
      scoreAbsent: attributeWithScores.scores[0].scoreAbsent,
      comment: attributeWithScores.scores[0].comment,
      importanceScore: importanceScore,
    };
  }

  transformNormalizedAttribute(
    normalized: NormalizedAttributeData,
    directusAttr: SubcriteriaAttributeDirectus,
    fulfillmentTypeMap: Map<number, AttributeFulfillmentTypeDirectus>,
  ): SubcriteriaAttribute {
    const fulfillmentType = directusAttr.fulfillment_type
      ? fulfillmentTypeMap.get(directusAttr.fulfillment_type)?.type
      : undefined;

    return {
      attributeId: normalized.attributeId,
      directusId: normalized.directusId,
      scaNumber: normalized.scaNumber,
      importanceScore: normalized.importanceScore,
      translations: directusAttr.translations || [],
      fulfillmentType,
      score: normalized.score,
      scoreAbsent: normalized.scoreAbsent,
      comment: normalized.comment,
      hasChanges: false,
    };
  }

  createDirectusMaps(directusData: {
    directusAttributes: SubcriteriaAttributeDirectus[];
    directusSubcriteria: EvaluationSubCriteriaDirectus[];
    fulfillmentTypes: AttributeFulfillmentTypeDirectus[];
  }): DirectusDataMaps {
    return {
      attributeMap: new Map(directusData.directusAttributes.map((attr) => [attr.id, attr])),
      subcriteriaMap: new Map(directusData.directusSubcriteria.map((sc) => [sc.id, sc])),
      fulfillmentTypeMap: new Map(directusData.fulfillmentTypes.map((ft) => [ft.id, ft])),
    };
  }

  async transformProviderScoresToTabs(
    providerScores: ProviderScoresResponse,
  ): Promise<SubcriteriaTab[]> {
    const directusData = await this.subcriteriaDataService.loadAllDirectusData();
    const maps = this.createDirectusMaps(directusData);

    const tabs: SubcriteriaTab[] = [];

    for (const criteria of providerScores.evaluationCriteria) {
      for (const subCriteria of criteria.subCriteria) {
        const tab = this.createTabFromProviderScores(subCriteria, maps);
        if (tab) {
          tabs.push(tab);
        }
      }
    }

    return tabs.sort((a, b) => a.scNumber - b.scNumber);
  }

  async transformProviderScoresToECTabs(providerScores: ProviderScoresResponse): Promise<ECTab[]> {
    const directusData = await this.subcriteriaDataService.loadAllDirectusData();
    const maps = this.createDirectusMaps(directusData);

    const evaluationCriteriaMap = new Map(
      directusData.directusEvaluationCriteria.map((ec) => [ec.id, ec]),
    );

    const ecTabs: ECTab[] = [];

    for (const criteria of providerScores.evaluationCriteria) {
      const subcriteriaTabsForThisEC: SubcriteriaTab[] = [];

      for (const subCriteria of criteria.subCriteria) {
        const tab = this.createTabFromProviderScores(subCriteria, maps);
        if (tab) {
          subcriteriaTabsForThisEC.push(tab);
        }
      }

      if (subcriteriaTabsForThisEC.length === 0) continue;

      const evaluationCriteriaInfo = evaluationCriteriaMap.get(criteria.directusId);
      if (!evaluationCriteriaInfo) continue;

      const ecTab: ECTab = {
        id: criteria.id,
        ecNumber: criteria.ecNumber,
        title: evaluationCriteriaInfo.name,
        directusId: criteria.directusId,
        translations: evaluationCriteriaInfo.translations || [],
        subcriteriaTabs: subcriteriaTabsForThisEC.sort((a, b) => a.scNumber - b.scNumber),
        isSaving: false,
      };

      if (isEC3(criteria.ecNumber)) {
        ecTab.moduleTabs = await this.tabManagementService.groupSubcriteriaByModulesForEC3(
          ecTab.subcriteriaTabs,
          maps,
        );
        ecTab.subcriteriaTabs = []; // Clear subcriteria tabs as they're now nested under modules
      }

      ecTabs.push(ecTab);
    }

    return ecTabs.sort((a, b) => a.ecNumber - b.ecNumber);
  }

  private groupTenderAttributesBySubcriteria(
    tenderAttributes: TenderSubcriteriaAttributeView[],
  ): Map<number, TenderSubcriteriaAttributeView[]> {
    const grouped = new Map<number, TenderSubcriteriaAttributeView[]>();

    for (const attr of tenderAttributes) {
      if (!grouped.has(attr.subcriteriaId)) {
        grouped.set(attr.subcriteriaId, []);
      }
      grouped.get(attr.subcriteriaId)!.push(attr);
    }

    return grouped;
  }

  createTabFromTenderAttributes(
    subcriteriaId: number,
    tenderAttributes: TenderSubcriteriaAttributeView[],
    maps: DirectusDataMaps,
    createAttributeFromTenderDataFn: (
      attr: TenderSubcriteriaAttributeView,
      maps: DirectusDataMaps,
    ) => SubcriteriaAttribute | null,
    options?: TabBuilderOptions,
  ): SubcriteriaTab | null {
    if (tenderAttributes.length === 0) return null;
    const firstAttribute = tenderAttributes[0];

    const directusAttribute = maps.attributeMap.get(firstAttribute.directusId);
    if (!directusAttribute?.subcriteria) return null;

    const subcriteriaInfo = maps.subcriteriaMap.get(directusAttribute.subcriteria);
    if (!subcriteriaInfo) return null;

    const enrichedAttributes: SubcriteriaAttribute[] = [];

    for (const tenderAttr of tenderAttributes) {
      const enrichedAttr = createAttributeFromTenderDataFn(tenderAttr, maps);
      if (enrichedAttr) {
        enrichedAttributes.push(enrichedAttr);
      }
    }

    const sortedAttributes = enrichedAttributes.sort((a, b) => a.scaNumber - b.scaNumber);

    return this.tabManagementService.buildSubcriteriaTab({
      id: subcriteriaId,
      subcriteriaInfo,
      attributes: sortedAttributes,
      options,
    });
  }

  createTemplateTenderData(
    directusAttributes: SubcriteriaAttributeDirectus[],
    directusSubcriteria: EvaluationSubCriteriaDirectus[],
    relevantECs: EvaluationCriteria[],
    scoresMap: Map<string, ScoreMapEntry>,
  ): TenderSubcriteriaAttributesResponse {
    const relevantSubcriteriaIds = new Set(relevantECs.flatMap((ec) => ec.subcriteria || []));

    const relevantAttributes = directusAttributes.filter((attr: SubcriteriaAttributeDirectus) =>
      relevantSubcriteriaIds.has(attr.subcriteria),
    );

    const subcriteriaIdMap = new Map<string, number>();
    directusSubcriteria.forEach((sc: EvaluationSubCriteriaDirectus, index: number) => {
      subcriteriaIdMap.set(sc.id, index + 1);
    });

    const attributes: TenderSubcriteriaAttributeView[] = relevantAttributes.map(
      (attr: SubcriteriaAttributeDirectus, index: number) => ({
        attributeId: index + 1,
        directusId: attr.id,
        scaNumber: attr.sca_number,
        subcriteriaId: subcriteriaIdMap.get(attr.subcriteria) || 0,
        importanceScore: EvaluationSubCriteriaImportanceScore.NOT_RELEVANT, // for templates: should not be shown in UI anyway
        score: scoresMap.get(attr.id)?.score,
        scoreAbsent: scoresMap.get(attr.id)?.scoreAbsent ?? false,
        comment: scoresMap.get(attr.id)?.comment,
        capabilityLevel: scoresMap.get(attr.id)?.capabilityLevel,
        provisioningType: scoresMap.get(attr.id)?.provisioningType,
        singleStage: scoresMap.get(attr.id)?.singleStage ?? false,
      }),
    );

    return {attributes};
  }

  private createTabFromProviderScores(
    subCriteriaWithScores: SubCriteriaWithScores,
    maps: DirectusDataMaps,
  ): SubcriteriaTab | null {
    const subcriteriaInfo = maps.subcriteriaMap.get(subCriteriaWithScores.directusId);
    if (!subcriteriaInfo) return null;

    const attributes: SubcriteriaAttribute[] = [];

    for (const attributeWithScores of subCriteriaWithScores.attributes) {
      const directusAttribute = maps.attributeMap.get(attributeWithScores.directusId);
      if (!directusAttribute) continue;

      const normalized = this.normalizeProviderScoreAttribute(attributeWithScores);
      if (!normalized) continue;

      const attribute = this.transformNormalizedAttribute(
        normalized,
        directusAttribute,
        maps.fulfillmentTypeMap,
      );

      attributes.push(attribute);
    }

    const sortedAttributes = attributes.sort((a, b) => a.scaNumber - b.scaNumber);

    if (sortedAttributes.length === 0) {
      return null;
    }

    return this.tabManagementService.buildSubcriteriaTab({
      id: subCriteriaWithScores.id,
      subcriteriaInfo,
      attributes: sortedAttributes,
    });
  }

  private createAttributeFromTenderData(
    tenderAttr: TenderSubcriteriaAttributeView,
    maps: DirectusDataMaps,
  ): SubcriteriaAttribute | null {
    const directusAttr = maps.attributeMap.get(tenderAttr.directusId);
    if (!directusAttr) return null;

    return this.transformTenderAttribute(tenderAttr, directusAttr, maps.fulfillmentTypeMap);
  }

  async transformTenderDataToECTabs(
    tenderData: TenderSubcriteriaAttributesResponse,
    options?: {supportedModuleFunctionIds?: number[]},
  ): Promise<ECTab[]> {
    const directusData = await this.subcriteriaDataService.loadAllDirectusData();
    const maps = this.createDirectusMaps(directusData);
    const ecTabs: ECTab[] = [];

    const evaluationCriteriaMap = new Map(
      directusData.directusEvaluationCriteria.map((ec) => [ec.id, ec]),
    );

    const groupedByEC = this.groupTenderAttributesByEC(
      tenderData.attributes,
      maps.subcriteriaMap,
      maps.attributeMap,
    );

    for (const [ecId, ecAttributes] of groupedByEC.entries()) {
      const ecTab = this.tabManagementService.createECTab(
        ecId,
        ecAttributes,
        maps,
        evaluationCriteriaMap,
        (attrs) => this.groupTenderAttributesBySubcriteria(attrs),
        (subcriteriaId, subAttributes, maps) =>
          this.createTabFromTenderAttributes(subcriteriaId, subAttributes, maps, (attr, maps) =>
            this.createAttributeFromTenderData(attr, maps),
          ),
      );

      if (ecTab) {
        if (isEC3(ecTab.ecNumber)) {
          ecTab.moduleTabs = await this.tabManagementService.groupSubcriteriaByModulesForEC3(
            ecTab.subcriteriaTabs,
            maps,
            options?.supportedModuleFunctionIds,
          );
          ecTab.subcriteriaTabs = [];
        }

        ecTabs.push(ecTab);
      }
    }

    return ecTabs.sort((a, b) => a.ecNumber - b.ecNumber);
  }

  private groupTenderAttributesByEC(
    tenderAttributes: TenderSubcriteriaAttributeView[],
    subcriteriaMap: Map<string, EvaluationSubCriteriaDirectus>,
    attributeMap: Map<string, SubcriteriaAttributeDirectus>,
  ): Map<string, TenderSubcriteriaAttributeView[]> {
    const grouped = new Map<string, TenderSubcriteriaAttributeView[]>();

    for (const attr of tenderAttributes) {
      const directusAttribute = attributeMap.get(attr.directusId);
      if (!directusAttribute?.subcriteria) continue;

      const subcriteriaInfo = subcriteriaMap.get(directusAttribute.subcriteria);
      if (!subcriteriaInfo?.criteria) continue;

      const ecId = subcriteriaInfo.criteria;
      if (!grouped.has(ecId)) {
        grouped.set(ecId, []);
      }
      grouped.get(ecId)!.push(attr);
    }

    return grouped;
  }

  transformBatchAttributeResult(
    batchResult: AttributesBySubCriteriaMap,
    subCriteriaConfigs: Array<{
      evaluationCriteriaId: number;
      subCriteriaId: number;
    }>,
  ): AttributesByEvaluationCriteriaMap {
    const transformedResult: AttributesByEvaluationCriteriaMap = {};

    for (const [subCriteriaId, attributes] of Object.entries(batchResult)) {
      const config = subCriteriaConfigs.find(
        (c) => c.subCriteriaId === Number.parseInt(subCriteriaId),
      );
      if (config) {
        if (!transformedResult[config.evaluationCriteriaId]) {
          transformedResult[config.evaluationCriteriaId] = {};
        }
        transformedResult[config.evaluationCriteriaId][Number.parseInt(subCriteriaId)] = attributes;
      }
    }

    return transformedResult;
  }

  async transformAverageScoresToBatchResult(
    evaluationCriteria: EvaluationCriteriaResponse[],
    subCriteriaConfigs: SubCriteriaConfig[],
  ): Promise<AttributesBySubCriteriaMap> {
    const result: AttributesBySubCriteriaMap = {};

    for (const config of subCriteriaConfigs) {
      const directusSubCriteriaId = config.subCriteriaData?.directusId;
      if (!directusSubCriteriaId) continue;

      const allDirectusAttributes =
        await this.directusService.loadSubCriteriaAttributesBySubCriteria(directusSubCriteriaId);

      const ec = evaluationCriteria.find((e) => e.id === config.evaluationCriteriaId);
      const sc = ec?.subCriteria?.find((s) => s.id === config.subCriteriaId);
      const backendAttributes = sc?.attributes || [];

      result[config.subCriteriaId] = allDirectusAttributes.map(
        (directusAttr: SubcriteriaAttributeDirectus): SubCriteriaAttributeItem => {
          const backendAttr = backendAttributes.find((attr) => attr.directusId === directusAttr.id);
          const averageScore = backendAttr?.importanceScores?.[0]?.importanceScore;

          return {
            id: backendAttr?.id,
            ecNumber: config.ecNumber,
            scNumber: config.subCriteriaData?.scNumber,
            directusId: directusAttr.id,
            title: directusAttr.id,
            translations: directusAttr.translations,
            scaNumber: backendAttr?.scaNumber ?? directusAttr.sca_number,
            selectedImportanceScore: averageScore,
            hasChanges: false,
            isSaving: false,
          };
        },
      );
    }
    return result;
  }
}
